import type { Notification } from "../types";

/** Wrap a phrase for Updates tile emphasis (`**…**` → bold; rest stays regular weight). */
export function emphasizePhrase(value: string): string {
  const safe = value.replace(/\*+/g, "").trim() || value;
  return `**${safe}**`;
}

/** Wrap a phrase for Updates tile italic (`__…__` → italic). */
export function italicizePhrase(value: string): string {
  const safe = value.replace(/_+/g, "").trim() || value;
  return `__${safe}__`;
}

export function pickTrimmed(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || null;
}

function defaultAlertDescription(notification: Notification, label: string): string {
  const tag = notification.alert_tag === "Board Alert"
    ? "Alert Description"
    : (notification.alert_tag ?? label);
  return notification.description ? `${tag}: ${notification.description}` : tag;
}

/** Linked record for reminder tiles — includes entity kind for copy. */
function reminderRecord(
  notification: Notification,
): { kind: "task" | "resource" | "client" | "checklist"; name: string } | null {
  const taskName = pickTrimmed(notification.task_name);
  if (notification.task != null || taskName) {
    return { kind: "task", name: taskName ?? "task" };
  }
  const resourceName = pickTrimmed(notification.resource_name);
  if (notification.resource != null || resourceName) {
    return { kind: "resource", name: resourceName ?? "resource" };
  }
  const clientName = pickTrimmed(notification.client_name);
  if (notification.client != null || clientName) {
    return { kind: "client", name: clientName ?? "client" };
  }
  const checklistName = pickTrimmed(notification.checklist_name);
  if (notification.checklist != null || checklistName) {
    return { kind: "checklist", name: checklistName ?? "checklist" };
  }
  return null;
}

export function reminderDescription(notification: Notification): string {
  const record = reminderRecord(notification);
  const detail = notification.description?.trim() || null;
  const title = notification.title?.trim() || null;

  if (record) {
    const target = `${record.kind} ${emphasizePhrase(record.name)}`;
    if (detail) return `Reminder for ${target} — ${detail}`;
    if (title && title.toLowerCase() !== record.name.toLowerCase()) {
      return `Reminder for ${target} — ${title}`;
    }
    return `Reminder for ${target} needs your attention.`;
  }

  if (title && detail) return `Reminder for ${emphasizePhrase(title)} — ${detail}`;
  if (title) return `Reminder for ${emphasizePhrase(title)} needs your attention.`;
  if (detail) return detail;
  return "Reminder needs your attention.";
}

/** Strip surrounding quotes from schedule assignee / subject names. */
function unquoteName(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return null;
  const matched = /^["'](.+)["']$/.exec(trimmed);
  const name = (matched?.[1] ?? trimmed).trim();
  return name || null;
}

/** Quoted segments inside schedule `description` payloads. */
function extractQuotedNames(value: string | null | undefined): string[] {
  if (!value) return [];
  return [...value.matchAll(/"([^"]+)"/g)]
    .map((match) => match[1]?.trim())
    .filter((name): name is string => Boolean(name));
}

/** Trailing "by 4 days" / "by 2 hours" style duration from schedule descriptions. */
function extractDurationPhrase(value: string | null | undefined): string | null {
  if (!value) return null;
  const matched = /\bby\s+(\d+)\s+(day|days|hour|hours|week|weeks)\b/i.exec(value);
  if (!matched) return null;
  const count = matched[1];
  const unit = matched[2].toLowerCase();
  return `${count} ${unit}`;
}

function taskPhrase(taskName: string | null, taskId: number | null): string | null {
  if (taskName) return `task ${emphasizePhrase(taskName)}`;
  if (taskId != null) return "a task";
  return null;
}

/**
 * Assignment alerts name the resource on `resource_name` and the task inside
 * quoted `description` (`task_name` is often null). When `resource_name` is
 * missing, a quoted description is still the resource and `task_name` is the task.
 */
function assignmentRecord(
  notification: Notification,
  quotedName: string | null,
): { subjectPhrase: string | null; taskName: string | null } {
  const resourceName = pickTrimmed(notification.resource_name);
  const explicitTaskName = pickTrimmed(notification.task_name);
  const taskName =
    explicitTaskName
    ?? (resourceName && quotedName && quotedName !== resourceName ? quotedName : null);
  const subject =
    resourceName
    ?? (quotedName && quotedName !== taskName ? quotedName : null)
    ?? pickTrimmed(notification.title);
  // Assignee is always a resource: resource_name on a resource card, quoted description on a task card.
  const subjectPhrase = subject ? `Resource ${emphasizePhrase(subject)}` : null;
  return { subjectPhrase, taskName };
}

/**
 * Schedule alert body — description-only tiles with bold subject/task names.
 * Common payloads:
 * - Event Assigned: description = `"Task"`, resource_name = assignee
 * - Event Removed: description = `"Task" from "Resource"`
 * - Event Extended: description = `"Task" by 4 days`, resource_name = assignee
 */
export function scheduleDescription(notification: Notification): string {
  const tag = (notification.alert_tag ?? "").trim();
  const tagKey = tag.toLowerCase();
  const rawDescription = pickTrimmed(notification.description);
  const quoted = extractQuotedNames(rawDescription);
  const soleQuoted = unquoteName(rawDescription);
  const taskName = pickTrimmed(notification.task_name) ?? quoted[0] ?? null;
  const creatorName = pickTrimmed(notification.creator_name);
  const byCreator = creatorName ? ` by ${emphasizePhrase(creatorName)}` : "";

  const taskRef = taskPhrase(taskName, notification.task);

  // Event Removed: `"Task" from "Resource"`
  if (tagKey.includes("removed")) {
    const removedTask = quoted[0] ?? taskName;
    const fromResource = quoted[1] ?? pickTrimmed(notification.resource_name);
    if (removedTask && fromResource) {
      return (
        `Task ${emphasizePhrase(removedTask)} was removed from resource ${emphasizePhrase(fromResource)}${byCreator}.`
      );
    }
    if (removedTask) {
      return `Task ${emphasizePhrase(removedTask)} was removed from the schedule${byCreator}.`;
    }
    if (taskRef) return `${taskRef} was removed from the schedule${byCreator}.`;
  }

  // Event Extended / Shortened: `"Task" by 4 days`, resource_name = assignee
  if (tagKey.includes("extended") || tagKey.includes("shortened") || tagKey.includes("reduced")) {
    const changedTask = quoted[0] ?? taskName;
    const duration = extractDurationPhrase(rawDescription);
    const verb = tagKey.includes("extended") ? "extended" : "shortened";
    const resourceName = pickTrimmed(notification.resource_name);
    const onResource =
      resourceName && resourceName !== changedTask
        ? ` on resource ${emphasizePhrase(resourceName)}`
        : "";
    if (changedTask && duration) {
      return (
        `Task ${emphasizePhrase(changedTask)} was ${verb} by ${duration}${onResource}${byCreator}.`
      );
    }
    if (changedTask) {
      return `Task ${emphasizePhrase(changedTask)} was ${verb}${onResource}${byCreator}.`;
    }
    if (taskRef) return `${taskRef} was ${verb}${onResource}${byCreator}.`;
  }

  // Event Unassigned — check before "assigned" (substring overlap).
  // Same payload as Event Assigned: resource_name is the assignee, quoted description is the task.
  if (tagKey.includes("unassigned")) {
    const { subjectPhrase, taskName: assignedTask } = assignmentRecord(
      notification,
      soleQuoted ?? quoted[0] ?? null,
    );
    const assignedTaskRef = taskPhrase(assignedTask, notification.task);
    if (subjectPhrase && assignedTaskRef) {
      return `${subjectPhrase} was unassigned from ${assignedTaskRef}${byCreator}.`;
    }
    if (subjectPhrase) return `${subjectPhrase} was unassigned on the schedule${byCreator}.`;
    if (assignedTaskRef) return `A resource was unassigned from ${assignedTaskRef}${byCreator}.`;
  }

  // Event Assigned: resource_name = "Developer", description = `"Rebuild and finish the restored kitchen"`.
  if (tagKey === "event assigned" || /\bassigned\b/.test(tagKey)) {
    const { subjectPhrase, taskName: assignedTask } = assignmentRecord(
      notification,
      soleQuoted ?? quoted[0] ?? null,
    );
    const assignedTaskRef = taskPhrase(assignedTask, notification.task);
    if (subjectPhrase && assignedTaskRef) {
      return `${subjectPhrase} was assigned to ${assignedTaskRef}${byCreator}.`;
    }
    if (subjectPhrase) return `${subjectPhrase} was assigned on the schedule${byCreator}.`;
    if (assignedTaskRef) return `A resource was assigned to ${assignedTaskRef}${byCreator}.`;
  }

  // Generic schedule change — keep tag context, emphasize known records.
  const subject =
    soleQuoted
    ?? quoted[0]
    ?? pickTrimmed(notification.resource_name)
    ?? pickTrimmed(notification.title);
  const clientName = pickTrimmed(notification.client_name);
  const resourceName = pickTrimmed(notification.resource_name) ?? quoted[1] ?? null;

  const parts: string[] = [];
  if (tag) parts.push(tag);
  if (subject) parts.push(emphasizePhrase(subject));
  if (taskRef && subject !== taskName) parts.push(`on ${taskRef}`);
  else if (taskRef && !subject) parts.push(taskRef);
  else if (clientName) parts.push(`for client ${emphasizePhrase(clientName)}`);
  else if (resourceName && resourceName !== subject) {
    parts.push(`for resource ${emphasizePhrase(resourceName)}`);
  }
  if (creatorName) parts.push(`by ${emphasizePhrase(creatorName)}`);

  if (parts.length > 0) {
    if (tag && subject && taskRef && subject !== taskName) {
      return `${tag}: ${emphasizePhrase(subject)} on ${taskRef}${byCreator}.`;
    }
    return `${parts.join(" ")}.`.replace(/\s+\./, ".");
  }

  return defaultAlertDescription(notification, "Schedule change");
}

/**
 * Status / entity-field alerts — description-only tiles with bold names.
 * Payloads: alert_tag names the field; description is the new value; task_name + creator_name when present.
 * Tags: Status, Team Assignment, Phase, Categorized as, Tagged (and similar).
 */
export function statusDescription(notification: Notification): string {
  const tag = pickTrimmed(notification.alert_tag) ?? "Status";
  const tagKey = tag.toLowerCase();
  const value = pickTrimmed(notification.description);
  const taskName = pickTrimmed(notification.task_name);
  const creatorName = pickTrimmed(notification.creator_name);
  const byCreator = creatorName ? ` by ${emphasizePhrase(creatorName)}` : "";
  const boldTask = taskName ? emphasizePhrase(taskName) : null;
  const boldValue = value ? emphasizePhrase(value) : null;
  const onTask = boldTask
    ? ` on task ${boldTask}`
    : notification.task != null
      ? " on a task"
      : "";

  if (tagKey === "status") {
    if (boldValue && boldTask) {
      return `Task ${boldTask} was set to ${boldValue}${byCreator}.`;
    }
    if (boldValue) return `Status was set to ${boldValue}${byCreator}.`;
    if (boldTask) return `Status changed on task ${boldTask}${byCreator}.`;
  }

  if (tagKey === "team assignment" || tagKey === "team") {
    if (boldValue && boldTask) {
      return `Task ${boldTask} was assigned to team ${boldValue}${byCreator}.`;
    }
    if (boldValue) return `Team set to ${boldValue}${byCreator}.`;
    if (boldTask) return `Team assignment changed on task ${boldTask}${byCreator}.`;
  }

  if (tagKey === "phase") {
    if (boldValue && boldTask) {
      return `Phase for task ${boldTask} was set to ${boldValue}${byCreator}.`;
    }
    if (boldValue) return `Phase was set to ${boldValue}${byCreator}.`;
    if (boldTask) return `Phase changed on task ${boldTask}${byCreator}.`;
  }

  if (tagKey === "categorized as" || tagKey.includes("categor")) {
    if (boldValue && boldTask) {
      return `Task ${boldTask} was categorized as ${boldValue}${byCreator}.`;
    }
    if (boldValue) return `Categorized as ${boldValue}${byCreator}.`;
    if (boldTask) return `Category changed on task ${boldTask}${byCreator}.`;
  }

  if (tagKey === "tagged" || tagKey === "tag" || tagKey === "untagged") {
    const verb = tagKey === "untagged" ? "untagged" : "tagged";
    if (boldValue && boldTask) {
      return `Task ${boldTask} was ${verb} ${boldValue}${byCreator}.`;
    }
    if (boldValue) return `${tag} ${boldValue}${byCreator}.`;
    if (boldTask) return `Tags changed on task ${boldTask}${byCreator}.`;
  }

  // Generic field change: "Client", "Priority", etc.
  if (boldValue && boldTask) {
    return `${tag} for task ${boldTask} was set to ${boldValue}${byCreator}.`;
  }
  if (boldValue) return `${tag}: ${boldValue}${byCreator}.`;
  if (onTask) return `${tag} changed${onTask}${byCreator}.`;
  if (tag) return `${tag}${byCreator}.`;
  return defaultAlertDescription(notification, "Status");
}

/** Parent entity phrase from FKs — task → resource → client. */
function parentEntityPhrase(notification: Notification): string | null {
  if (notification.task != null) {
    const name = pickTrimmed(notification.task_name);
    return name ? `task ${emphasizePhrase(name)}` : "a task";
  }
  if (notification.resource != null) {
    const name = pickTrimmed(notification.resource_name);
    return name ? `resource ${emphasizePhrase(name)}` : "a resource";
  }
  if (notification.client != null) {
    const name = pickTrimmed(notification.client_name);
    return name ? `client ${emphasizePhrase(name)}` : "a client";
  }
  return null;
}

/**
 * Nudges category — checklist / checked-item / note alerts.
 * Description-only tiles; bold checklist/note/item and parent entity names.
 */
export function nudgeDescription(notification: Notification): string {
  const tag = pickTrimmed(notification.alert_tag) ?? "";
  const tagKey = tag.toLowerCase();
  const rawDescription = pickTrimmed(notification.description);
  const quoted = extractQuotedNames(rawDescription);
  const subject = quoted[0] ?? null;
  const checklistName = pickTrimmed(notification.checklist_name);
  const creatorName = pickTrimmed(notification.creator_name);
  const byCreator = creatorName ? ` by ${emphasizePhrase(creatorName)}` : "";
  const parent = parentEntityPhrase(notification);

  // Ignore useless bodies that just repeat the tag (e.g. "Checklist Nudge").
  const usefulDescription =
    rawDescription
    && rawDescription.toLowerCase() !== tagKey
      ? rawDescription
      : null;

  // Checklist Nudge
  if (tagKey.includes("nudge")) {
    if (checklistName && parent) {
      return `Checklist ${emphasizePhrase(checklistName)} on ${parent} needs attention${byCreator}.`;
    }
    if (checklistName) {
      return `Checklist ${emphasizePhrase(checklistName)} needs attention${byCreator}.`;
    }
    if (parent) return `A checklist on ${parent} needs attention${byCreator}.`;
    return `Checklist nudge${byCreator}.`;
  }

  // Checked item on a checklist
  if (tagKey.includes("checked")) {
    const item = subject;
    if (item && checklistName) {
      return (
        `${emphasizePhrase(item)} was checked on checklist ${emphasizePhrase(checklistName)}${byCreator}.`
      );
    }
    if (item && parent) {
      return `${emphasizePhrase(item)} was checked on ${parent}${byCreator}.`;
    }
    if (item) return `${emphasizePhrase(item)} was checked${byCreator}.`;
    if (checklistName) {
      return `An item was checked on checklist ${emphasizePhrase(checklistName)}${byCreator}.`;
    }
  }

  // Note Added
  if (tagKey.includes("note") || notification.note != null) {
    return noteDescription(notification);
  }

  // Checklist Added
  if (tagKey.includes("checklist") || notification.checklist != null) {
    const name = checklistName ?? subject;
    if (name && parent) {
      return `Checklist ${emphasizePhrase(name)} was added to ${parent}${byCreator}.`;
    }
    if (name) return `Checklist ${emphasizePhrase(name)} was added${byCreator}.`;
    if (parent) return `A checklist was added to ${parent}${byCreator}.`;
  }

  if (usefulDescription && parent) {
    return `${usefulDescription} on ${parent}${byCreator}.`;
  }
  if (usefulDescription) return `${usefulDescription}${byCreator}.`;
  if (tag && parent) return `${tag} on ${parent}${byCreator}.`;
  if (tag) return `${tag}${byCreator}.`;
  return defaultAlertDescription(notification, "Nudge");
}

/**
 * Notes category — note added on a task, resource, or client.
 * Description-only; bold note title + parent entity; creator from creator_name.
 */
export function noteDescription(notification: Notification): string {
  const rawDescription = pickTrimmed(notification.description);
  const quoted = extractQuotedNames(rawDescription);
  const noteTitle = quoted[0] ?? pickTrimmed(notification.title);
  const creatorName = pickTrimmed(notification.creator_name);
  const byCreator = creatorName ? ` by ${emphasizePhrase(creatorName)}` : "";
  const parent = parentEntityPhrase(notification);

  if (noteTitle && parent) {
    return `Note ${emphasizePhrase(noteTitle)} was added to ${parent}${byCreator}.`;
  }
  if (noteTitle) return `Note ${emphasizePhrase(noteTitle)} was added${byCreator}.`;
  if (parent) return `A note was added to ${parent}${byCreator}.`;
  return `Note added${byCreator}.`;
}

/**
 * Files category — file added/updated on a task, resource, or client.
 * Description-only; bold filename + parent entity; creator from creator_name.
 */
export function fileDescription(notification: Notification): string {
  const tag = pickTrimmed(notification.alert_tag) ?? "File";
  const tagKey = tag.toLowerCase();
  const rawDescription = pickTrimmed(notification.description);
  const quoted = extractQuotedNames(rawDescription);
  const fileName =
    quoted[0]
    ?? pickTrimmed(notification.title)
    ?? null;
  const creatorName = pickTrimmed(notification.creator_name);
  const byCreator = creatorName ? ` by ${emphasizePhrase(creatorName)}` : "";
  const parent = parentEntityPhrase(notification);

  const verb = tagKey.includes("removed") || tagKey.includes("deleted")
    ? "removed from"
    : tagKey.includes("updated") || tagKey.includes("replaced")
      ? "updated on"
      : "added to";

  if (fileName && parent) {
    return `File ${emphasizePhrase(fileName)} was ${verb} ${parent}${byCreator}.`;
  }
  if (fileName) {
    const alone =
      verb === "added to" ? "added"
        : verb === "removed from" ? "removed"
          : "updated";
    return `File ${emphasizePhrase(fileName)} was ${alone}${byCreator}.`;
  }
  if (parent) return `A file was ${verb} ${parent}${byCreator}.`;
  if (tag) return `${tag}${byCreator}.`;
  return defaultAlertDescription(notification, "File");
}

/**
 * Board alert — inbox message from a board user to recipients (incl. themselves).
 * Description-only; bold sender, subject, and quoted message body.
 */
export function boardAlertDescription(notification: Notification): string {
  const title = pickTrimmed(notification.title);
  const body = pickTrimmed(notification.description);
  const creatorName = pickTrimmed(notification.creator_name);
  const sender = creatorName ? emphasizePhrase(creatorName) : "A board user";
  const quotedBody = body ? italicizePhrase(`"${body}"`) : null;

  if (title && quotedBody) {
    return `${sender} sent you a board message: ${emphasizePhrase(title)} — ${quotedBody}.`;
  }
  if (title) {
    return `${sender} sent you a board message: ${emphasizePhrase(title)}.`;
  }
  if (quotedBody) {
    return `${sender} sent you a board message — ${quotedBody}.`;
  }
  return `${sender} sent you a board message.`;
}

export function isStatusCategoryNotification(notification: Notification): boolean {
  return (notification.category ?? "").trim().toLowerCase() === "status";
}

/** Board label from notification root or async meta (null when unknown). */
export function resolveBoardLabel(notification: Notification): string | null {
  const fromRoot = pickTrimmed(notification.board_name);
  if (fromRoot) return fromRoot;
  const meta = notification.async_task?.meta_data;
  if (!meta || typeof meta !== "object") return null;
  const value = (meta as Record<string, unknown>).board_name;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/**
 * Non-status GENERAL alerts — description-only with bold subject / creator.
 * Status-shaped GENERAL payloads use `statusDescription` instead.
 */
export function generalDescription(notification: Notification): string {
  const tag = pickTrimmed(notification.alert_tag);
  const title = pickTrimmed(notification.title);
  const detail = pickTrimmed(notification.description);
  const creatorName = pickTrimmed(notification.creator_name);
  const fromCreator = creatorName ? ` from ${emphasizePhrase(creatorName)}` : "";
  const taskName = pickTrimmed(notification.task_name);
  const subject =
    title && tag && title.toLowerCase() === tag.toLowerCase()
      ? null
      : title;

  if (subject && detail && taskName) {
    return `${emphasizePhrase(subject)} on task ${emphasizePhrase(taskName)} — ${detail}${fromCreator}.`;
  }
  if (subject && detail) {
    return `${emphasizePhrase(subject)} — ${detail}${fromCreator}.`;
  }
  if (subject && taskName) {
    return `${emphasizePhrase(subject)} on task ${emphasizePhrase(taskName)}${fromCreator}.`;
  }
  if (subject) return `${emphasizePhrase(subject)}${fromCreator}.`;
  if (tag && detail && taskName) {
    return `${tag} on task ${emphasizePhrase(taskName)} — ${detail}${fromCreator}.`;
  }
  if (tag && detail) return `${tag} — ${detail}${fromCreator}.`;
  if (detail && taskName) {
    return `${detail} on task ${emphasizePhrase(taskName)}${fromCreator}.`;
  }
  if (detail) return `${detail}${fromCreator}.`;
  if (tag) return `${tag}${fromCreator}.`;
  return creatorName
    ? `General update from ${emphasizePhrase(creatorName)}.`
    : "General update.";
}

/**
 * Leads alerts — description-only with bold lead name / creator.
 * Uses title as the lead label when present; alert_tag for the event kind.
 */
export function leadsDescription(notification: Notification): string {
  const tag = pickTrimmed(notification.alert_tag) ?? "Lead";
  const tagKey = tag.toLowerCase();
  const leadName =
    pickTrimmed(notification.title)
    ?? pickTrimmed(notification.client_name);
  const detail = pickTrimmed(notification.description);
  const creatorName = pickTrimmed(notification.creator_name);
  const byCreator = creatorName ? ` by ${emphasizePhrase(creatorName)}` : "";
  const boldLead = leadName ? emphasizePhrase(leadName) : null;

  if (tagKey.includes("new") || tagKey === "lead created" || tagKey === "created") {
    if (boldLead && detail) return `New lead ${boldLead} — ${detail}${byCreator}.`;
    if (boldLead) return `New lead ${boldLead}${byCreator}.`;
    if (detail) return `New lead — ${detail}${byCreator}.`;
    return `New lead${byCreator}.`;
  }

  if (tagKey.includes("updat") || tagKey.includes("chang")) {
    if (boldLead && detail) return `Lead ${boldLead} was updated — ${detail}${byCreator}.`;
    if (boldLead) return `Lead ${boldLead} was updated${byCreator}.`;
    if (detail) return `A lead was updated — ${detail}${byCreator}.`;
    return `A lead was updated${byCreator}.`;
  }

  if (boldLead && detail) return `Lead ${boldLead} — ${detail}${byCreator}.`;
  if (boldLead) return `Lead ${boldLead} needs attention${byCreator}.`;
  if (detail) return `${tag} — ${detail}${byCreator}.`;
  return `${tag}${byCreator}.`;
}
